import { Flex, FlexColumn, ThemeType } from '@equifinance/shared';
import styled from 'styled-components';
import TaskOverview from './TaskOverview';
import ChecklistOverview from './ChecklistOverview';
import { Application } from '../../../contexts/applications';
import { EquifaxAccordionHeaderShadow } from '../../Credit/CreditDetails/CreditDetails';
import { Stages } from '../../../types/Stages';

const ExpandableContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[7]};
  padding: ${theme.padding[5]} ${theme.padding[6]};
  background: ${theme.colours.neutral[9]};
  font-size: ${theme.fontSize[1]};
  position: relative;
  color: ${theme.colours.neutral[2]};
`}`;

const HeaderShadow = styled(EquifaxAccordionHeaderShadow)`${({ theme }) => `
  top: -${theme.padding[1]};
  background: linear-gradient(180deg, ${theme.colours.neutral[7]} 0%, ${theme.colours.neutral[9]} 100%);
  left: 0;
`}`;

export const OverviewContainer = styled.div`${({ theme }) => `
  border-radius: ${theme.padding[3]};
  box-shadow: ${theme.boxShadow[1]};
  overflow: hidden;
`}`;

export const TaskOverviewContainer = styled(OverviewContainer)`
  flex-basis: 65%;
`;

export const ChecklistOverviewContainer = styled(OverviewContainer)`
  flex-basis: 35%;
`;

export const OverviewHeader = styled.div`${({ theme }) => `
  background: ${theme.colours.primary[3]};
  color: ${theme.colours.neutral[10]};
  padding: ${theme.padding[2]} ${theme.padding[5]};
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[2]};
`}`;

export const OverviewBody = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[5]};
  gap: ${theme.padding[3]};
`}`;

export const OverviewCell = styled(Flex)<{ fontWeight?: keyof ThemeType['fontWeight'] }>`${({ theme, fontWeight }) => `
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  max-width: 100%;
  padding: 0 ${theme.padding[4]};
  min-width: ${theme.padding[11]};
  align-items: center;
  font-weight: ${fontWeight || theme.fontWeight.bold};
  
  &:not(:last-of-type) {
    border-right: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
  }
`}`;

const Row = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[1]};
  flex-grow: 1;
`}`;

export const HeadRow = styled(Row)`${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  border-radius: ${theme.borderRadius[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

export const GroupRow = styled(HeadRow)`${({ theme }) => `
  background: ${theme.colours.tertiary[6]};
  color: ${theme.colours.tertiary[1]};
  letter-spacing: ${theme.borderWidth[1]};
  text-transform: uppercase;
`}`;

export const BodyRow = styled(Row)`${({ theme }) => `
  padding: ${theme.padding[3]} ${theme.padding[1]};
  border-bottom: ${theme.borderWidth[1]} solid ${theme.colours.neutral[7]};

  ${OverviewCell} {
    padding: ${theme.padding[1]} ${theme.padding[4]};
  }
`}`;

export const GroupList = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[5]};
`}`;

export function groupItemsByStage<T extends { stage: Stages }, R>(
  items: T[],
  transform: (item: T) => R,
) {
  return items.reduce((grouped, item) => {
    const { stage } = item;
    if (stage) {
      grouped[stage] = grouped[stage] || [];
      grouped[stage].push(transform(item));
    }
    return grouped;
  }, {} as Record<Stages, R[]>);
}

export default function ExpandableContent({ data }: { data: Application }) {
  return (
    <ExpandableContainer>
      <HeaderShadow />
      <TaskOverview tasks={data.tasks} />
      <ChecklistOverview checklists={data.checklists} />
    </ExpandableContainer>
  );
}
